import { useRef } from "react";
import { ImagePlus, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { IconeColorido } from "@/components/comecar/selecao/IconeColorido";
import { iconeDaCategoria } from "@/components/comecar/selecao/icones-da-selecao";
import { reduzirImagem } from "@/lib/reduzir-imagem";

/** O "perfil" do serviço no topo do diálogo: foto, categoria, nome e descrição, ao vivo com o que
 *  a pessoa digita embaixo. Sem foto, o ícone da categoria. */
export function CabecalhoDoServico({
  imagem,
  carregando,
  categoria,
  nome,
  descricao,
  onImagem,
}: {
  imagem: string | null;
  carregando: boolean;
  categoria: string;
  nome: string;
  descricao: string;
  onImagem: (dataUrl: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const icone = iconeDaCategoria(categoria);

  const escolher = async (file: File) => {
    if (!file.type.startsWith("image/")) return toast.error("Selecione um arquivo de imagem.");
    try {
      onImagem(await reduzirImagem(file));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-brand-100 bg-brand-50/60 p-4 sm:flex-row sm:items-start dark:border-primary/20 dark:bg-primary/5">
      <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white bg-white shadow-sm dark:border-border dark:bg-card">
        {carregando ? (
          <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
        ) : imagem ? (
          <img src={imagem} alt={`Foto de ${nome || "serviço"}`} className="h-full w-full object-cover" />
        ) : (
          <IconeColorido {...icone} tamanho="xl" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: `${icone.cor}1a`, color: icone.cor }}>
          <IconeColorido {...icone} tamanho="sm" />
          {categoria || "Sem categoria"}
        </span>
        <p className="mt-1.5 truncate text-lg font-semibold text-zinc-900 dark:text-foreground">{nome.trim() || "Novo serviço"}</p>
        <p className="mt-0.5 line-clamp-2 text-sm text-zinc-600 dark:text-muted-foreground">
          {descricao.trim() || "Sem descrição. Escreva abaixo o que o serviço é — sai no catálogo e no link público."}
        </p>
      </div>

      <div className="flex shrink-0 gap-1.5 sm:flex-col sm:items-end">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-brand-700 shadow-sm hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-primary"
        >
          {imagem ? <Pencil className="h-3.5 w-3.5" /> : <ImagePlus className="h-3.5 w-3.5" />}
          {imagem ? "Alterar imagem" : "Adicionar imagem"}
        </button>
        {imagem && (
          <button
            type="button"
            onClick={() => onImagem(null)}
            className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-xs font-medium text-zinc-500 hover:text-red-600 sm:h-auto"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remover
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) void escolher(file);
        }}
      />
    </div>
  );
}
